import React, { createContext, useContext, useMemo } from 'react';

import {
  FavoritedMatterData,
  useFavoritedMatters,
} from '@/hooks/useFavoritedMatters';

interface FavoritedMattersContextType {
  /** Check if a matter is favorited */
  isFavorited: (matterId: string) => boolean;
  /** Toggle the favorite status of a matter */
  toggleFavorite: (matterId: string, matterName: string) => void;
  /** Add a matter to favorites */
  addFavorite: (matterId: string, matterName: string) => void;
  /** Remove a matter from favorites */
  removeFavorite: (matterId: string) => void;
  /** Get all favorited matter IDs as a Set */
  getFavoritedIds: () => Set<string>;
  /** Get all favorited matters with their data */
  getAllFavorites: () => FavoritedMatterData[];
  /** Clear all favorites */
  clearAllFavorites: () => void;
  /** Number of favorited matters */
  favoritedCount: number;
}

const FavoritedMattersContext =
  createContext<FavoritedMattersContextType | null>(null);

interface FavoritedMattersProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component for favorited matters state.
 * Wrap your app or a section of it with this to enable the favorites feature.
 */
export const FavoritedMattersProvider: React.FC<
  FavoritedMattersProviderProps
> = ({ children }) => {
  const {
    isFavorited,
    toggleFavorite,
    addFavorite,
    removeFavorite,
    getFavoritedIds,
    getAllFavorites,
    clearAllFavorites,
    favoritedCount,
  } = useFavoritedMatters();

  const value = useMemo(
    () => ({
      isFavorited,
      toggleFavorite,
      addFavorite,
      removeFavorite,
      getFavoritedIds,
      getAllFavorites,
      clearAllFavorites,
      favoritedCount,
    }),
    [
      isFavorited,
      toggleFavorite,
      addFavorite,
      removeFavorite,
      getFavoritedIds,
      getAllFavorites,
      clearAllFavorites,
      favoritedCount,
    ],
  );

  return (
    <FavoritedMattersContext.Provider value={value}>
      {children}
    </FavoritedMattersContext.Provider>
  );
};

/**
 * Hook to access the favorited matters context.
 * Must be used within a FavoritedMattersProvider.
 */
export const useFavoritedMattersContext = () => {
  const context = useContext(FavoritedMattersContext);
  if (!context) {
    throw new Error(
      'useFavoritedMattersContext must be used within a FavoritedMattersProvider',
    );
  }
  return context;
};

/**
 * Optional hook that returns null if used outside of provider.
 * Useful for components that may or may not have the provider available.
 */
export const useFavoritedMattersContextOptional = () => {
  return useContext(FavoritedMattersContext);
};
